import React, { useState, useEffect } from 'react';
import { ArrowLeft, CheckCircle2, Info, RotateCcw, Trash2 } from 'lucide-react';
import { MainLayout } from './MainLayout';
import { useApp } from './AppContext';
import { useTranslation } from '../i18n/TranslationContext';

export function IDResult() {
  const { 
    setCurrentScreen, 
    idCheckState, 
    deleteIDDocument, 
    useReplacement,
    startReplacement 
  } = useApp();
  const { t } = useTranslation();
  
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showReplacementSuccess, setShowReplacementSuccess] = useState(false);

  // Show replacement success message if coming from replacement flow
  useEffect(() => {
    if (idCheckState.navigationContext === 'replacement') {
      setShowReplacementSuccess(true);
      // Hide the message after 3 seconds
      const timer = setTimeout(() => {
        setShowReplacementSuccess(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [idCheckState.navigationContext]);

  const handleBack = () => {
    setCurrentScreen('dashboard');
  };

  const handleContinue = () => {
    setCurrentScreen('dashboard');
  };

  const handleReplacePhoto = () => {
    if (idCheckState.replacementsRemaining > 0) {
      startReplacement();
      setCurrentScreen('id-capture');
    }
  };

  const handleDeleteDocument = () => {
    setShowDeleteDialog(true);
  };

  const confirmDeleteDocument = () => {
    deleteIDDocument();
    setShowDeleteDialog(false);
    setCurrentScreen('dashboard');
  };

  return (
    <>
      <MainLayout showBack onBack={handleBack} showBottomNav={false}>
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-[var(--ucm-text-dark)]">
            {t('idResult.title')}
          </h1>
        </div>

        {/* Replacement Success Notification */}
        {showReplacementSuccess && (
          <div className="bg-[var(--ucm-light-green)] border border-[var(--ucm-success-green)] rounded-xl p-4 mb-4">
            <div className="flex items-center space-x-3">
              <CheckCircle2 className="w-5 h-5 text-[var(--ucm-success-green)] flex-shrink-0" />
              <p className="text-sm font-medium text-[var(--ucm-dark-green)]">
                {t('success.updated')}
              </p>
            </div>
          </div>
        )}

        {/* Success Section */}
        <div className="bg-white rounded-2xl p-6 shadow-[0_4px_16px_rgba(0,0,0,0.08)] mb-6">
          <div className="text-center mb-6">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--ucm-light-green)] rounded-full mb-4">
              <CheckCircle2 className="w-8 h-8 text-[var(--ucm-success-green)]" />
            </div>
            
            <h3 className="font-semibold text-[var(--ucm-text-dark)] mb-2">
              {t('idResult.verified')}
            </h3>
            
            <p className="text-[var(--ucm-text-medium)] leading-relaxed">
              {showReplacementSuccess 
                ? t('idResult.newDocumentVerified')
                : t('idResult.documentVerified')
              }
            </p>
          </div>

          {/* Document Display */}
          <div className="bg-[var(--ucm-bg-gray)] rounded-xl p-4 mb-6">
            <div className="flex items-start space-x-4">
              {/* Document Thumbnail */}
              <div className="w-16 h-12 bg-gradient-to-br from-blue-100 to-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                <div className="w-10 h-7 bg-white/80 rounded-sm flex items-center justify-center shadow-sm">
                  <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                </div>
              </div>
              
              {/* Document Info */}
              <div className="flex-1 min-w-0">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-[var(--ucm-text-dark)]">
                    {t('idResult.documentTypeLabel')}
                  </p>
                  <p className="text-sm text-[var(--ucm-text-medium)]">
                    {idCheckState.uploadDate ? t('idResult.uploadDate', { date: idCheckState.uploadDate }) : t('idResult.uploadDateUnknown')}
                  </p>
                  <p className="text-sm text-[var(--ucm-text-medium)]">
                    {t('idResult.status')}
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-[var(--ucm-light-green)] text-[var(--ucm-dark-green)] ml-1">
                      {t('idResult.verifiedStatus')}
                    </span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Replacement Info */}
          <div className="bg-[var(--ucm-light-blue)] rounded-xl p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Info className="w-5 h-5 text-[var(--ucm-info-blue)] flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-[var(--ucm-text-dark)] mb-1">
                  {t('idResult.replacementInfo')}
                </p>
                <p className={`text-sm font-medium ${
                  idCheckState.replacementsRemaining === 0 
                    ? 'text-[var(--ucm-error-red)]' 
                    : 'text-[var(--ucm-dark-blue)]'
                }`}>
                  {idCheckState.replacementsRemaining === 0 
                    ? t('idResult.noReplacementsLeft') 
                    : t('idResult.replacementsRemaining', { count: idCheckState.replacementsRemaining })
                  }
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="space-y-3">
          <button
            onClick={handleReplacePhoto}
            disabled={idCheckState.replacementsRemaining === 0}
            className={`w-full h-14 font-semibold rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 flex items-center justify-center space-x-2 ${
              idCheckState.replacementsRemaining === 0
                ? 'bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-light)] cursor-not-allowed'
                : 'bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-dark)] hover:bg-[var(--ucm-border-medium)] focus:ring-[var(--ucm-border-medium)]'
            }`}
          >
            <RotateCcw className="w-5 h-5" />
            <span>{t('idResult.retry')}</span>
          </button>
          
          <button
            onClick={handleContinue}
            className="w-full h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2"
          >
            {t('common.continue')}
          </button>

          {/* Delete Document Button */}
          <button
            onClick={handleDeleteDocument}
            className="w-full h-12 text-[var(--ucm-error-red)] font-medium rounded-full hover:bg-[var(--ucm-error-red)]/5 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-error-red)] focus:ring-offset-2 flex items-center justify-center space-x-2"
          >
            <Trash2 className="w-4 h-4" />
            <span>{t('common.delete')}</span>
          </button>
        </div>
      </MainLayout>

      {/* Simple Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="dialog-overlay">
          <div className="dialog-card">
            <h2 className="text-xl font-bold text-[var(--ucm-text-dark)] text-center mb-4">
              {t('idResult.deleteDialog.title')}
            </h2>
            
            <p className="text-[var(--ucm-text-medium)] text-center mb-6">
              {t('idResult.deleteDialog.message')}
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                className="flex-1 h-12 bg-[var(--ucm-disabled-gray)] text-[var(--ucm-text-dark)] font-medium rounded-full hover:bg-[var(--ucm-border-medium)] transition-colors duration-200"
              >
                {t('idResult.deleteDialog.cancel')}
              </button>
              <button
                onClick={confirmDeleteDocument}
                className="flex-1 h-12 bg-[var(--ucm-error-red)] text-white font-medium rounded-full hover:bg-red-600 transition-colors duration-200"
              >
                {t('idResult.deleteDialog.confirm')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}